import type {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material';

/**
 * Comportamiento de foco/blur de los autocompletes de destino del buscador
 * (#2226). Una sola definición (21.22) para el picker de lugares
 * (`PlaceAutocomplete`, `CircuitDestinationsPlaceInput`) y para los pickers
 * nativos de cada vertical (estaciones, paradas, puertos, ubicaciones de
 * autos, destinos de experiencias, ciudades de circuitos, destinos de
 * eSIMs): son el MISMO control para el asesor, así que no pueden
 * comportarse distinto según el vertical en el que esté.
 *
 * El estado separa DOS cosas que no son la misma (y confundirlas fue el bug
 * del primer intento): **tener el foco** y **estar tipeando una búsqueda**.
 * Elegir una opción cierra la búsqueda pero NO devuelve el foco: MUI deja el
 * input enfocado, mostrando la etiqueta de lo elegido, y el asesor puede
 * seguir tecleando ahí mismo sin pasar por un `blur`.
 *
 * Cuatro invariantes, en el orden en que se viven:
 *
 * 1. **Enfocar un campo con selección hecha deja tipear otra búsqueda de
 *    una**: el input arranca vacío. MUI solo selecciona el texto al
 *    enfocar con el MOUSE (`selectOnFocus` vive en su `handleClick`); con
 *    Tab el cursor cae al final de la etiqueta y lo tipeado se le pega
 *    ("Madrid" + "bar" = "Madridbar"), que no matchea nada.
 * 2. **Salir sin elegir devuelve la selección anterior**, y eso incluye el
 *    VALOR, no solo el texto: MUI borra el valor solo por quedar el input
 *    vacío (`handleInputChange` llama a `handleValue(null, 'clear')`), así
 *    que borrar con la tecla para buscar otra cosa y salir sin elegir
 *    dejaba el campo vacío sin que nadie lo pidiera. El borrado explícito
 *    sigue siendo la X, que se distingue porque se anuncia con un
 *    `onInputChange` de razón `clear` inmediatamente antes.
 *    "Salir del campo" es salir del CONTROL: el foco que se va a la X o a
 *    la flecha del desplegable no cuenta (lo resuelve `focusCrossesTheControl`).
 * 3. **El spinner del campo se enciende solo por SU sesión de tipeo**: las
 *    escrituras programáticas del valor (restaurar la URL, invertir origen
 *    y destino, elegir una opción) llegan como `reason: 'reset'` y no
 *    disparan búsqueda; al salir del campo la query queda vacía.
 * 4. **Seguir tipeando después de elegir, sin salir del campo, arranca una
 *    búsqueda LIMPIA**: el input quedó mostrando la etiqueta elegida y MUI
 *    reporta el contenido entero del campo, así que lo tecleado llega
 *    pegado a esa etiqueta. La sesión de tipeo nueva se queda solo con lo
 *    tecleado (`textTypedOverSelection`), nunca con la etiqueta adelante.
 *
 * Lógica pura, sin React: el jest de este repo no resuelve `.tsx` a
 * propósito (ver `jest.config.cjs` y el docstring de `PlaceAutocomplete`),
 * así que todo lo testeable tiene que poder importarse desde un `.ts`. El
 * cableado con MUI vive en `useAutocompleteFocus.ts`.
 */
export interface AutocompleteFocusState {
  /**
   * El control tiene el foco. Se sostiene aunque la sesión de tipeo se
   * cierre: elegir una opción deja el input enfocado (invariante 4).
   */
  focused: boolean;
  /**
   * El asesor está tipeando en este campo: el input muestra SU texto, no la
   * etiqueta de la selección.
   */
  editing: boolean;
  /** Lo tipeado en esta sesión de edición. */
  text: string;
  /**
   * El último cambio de input fue la X de limpiar, no tecleo. MUI usa la
   * MISMA razón (`clear`) para el botón y para el vaciado accidental por
   * teclado, y es lo único que los distingue: el botón emite
   * `onInputChange(_, '', 'clear')` justo antes del `onChange`, mientras que
   * el teclado emite `onInputChange(_, '', 'input')`.
   */
  clearRequested: boolean;
}

/** Sin foco ni sesión de tipeo: el input muestra la etiqueta de la selección. */
export const AUTOCOMPLETE_FOCUS_IDLE: AutocompleteFocusState = {
  focused: false,
  editing: false,
  text: '',
  clearRequested: false,
};

export type AutocompleteFocusEvent =
  | { type: 'focus' }
  | { type: 'blur' }
  | {
      type: 'input';
      text: string;
      reason: AutocompleteInputChangeReason;
      /**
       * Lo que el input mostraba en reposo, o sea la etiqueta de lo elegido:
       * es lo que hay que descontar de `text` cuando la sesión de tipeo
       * arranca sobre un campo que ya tenía selección (invariante 4).
       */
      selectedLabel: string;
    }
  | { type: 'valueChange'; reason: AutocompleteChangeReason };

/**
 * Cierra la sesión de tipeo sin tocar el foco: el input vuelve a mostrar la
 * etiqueta de lo elegido, pero el campo sigue enfocado si lo estaba.
 */
function restingWhileFocused(state: AutocompleteFocusState): AutocompleteFocusState {
  if (!state.editing && !state.clearRequested && state.text === '') return state;
  return {
    focused: state.focused,
    editing: false,
    text: '',
    clearRequested: false,
  };
}

/**
 * Devuelve el MISMO objeto cuando la transición no cambia nada: el hook lo
 * usa para no re-renderizar ni re-emitir la query por eventos inocuos (un
 * `reset` con el campo ya en reposo, un blur repetido).
 */
export function nextAutocompleteFocusState(
  state: AutocompleteFocusState,
  event: AutocompleteFocusEvent,
): AutocompleteFocusState {
  // Sin `default` a propósito: si mañana aparece un evento nuevo, TypeScript
  // marca que hay un camino sin `return` en vez de tragárselo en silencio.
  switch (event.type) {
  case 'focus':
    return state.focused && state.editing && state.text === '' && !state.clearRequested
      ? state
      : { focused: true, editing: true, text: '', clearRequested: false };
  case 'blur':
    return state.focused || state.editing ? AUTOCOMPLETE_FOCUS_IDLE : state;
  case 'input':
    // `reset` es MUI reponiendo el input por su cuenta (eligió una opción,
    // perdió el foco, o el valor cambió desde afuera): cierra la sesión de
    // tipeo, nunca la abre — si lo tratáramos como texto nuevo, restaurar
    // una búsqueda de la URL o invertir origen/destino dispararía una
    // consulta al catálogo por la etiqueta que el propio componente acaba
    // de escribir. El foco queda como estaba: elegir no lo devuelve.
    if (event.reason === 'reset') {
      return restingWhileFocused(state);
    }
    if (event.reason === 'clear') {
      return { focused: true, editing: true, text: '', clearRequested: true };
    }
    return {
      focused: true,
      editing: true,
      // Si ya había sesión de tipeo, `text` ES lo tipeado. Si no, el input
      // venía mostrando la etiqueta de lo elegido y hay que descontarla
      // (invariante 4).
      text: state.editing
        ? event.text
        : textTypedOverSelection(event.text, event.selectedLabel),
      clearRequested: false,
    };
  case 'valueChange':
    // Elegir (o vaciar con la X) cierra la sesión: manda la etiqueta del
    // nuevo valor. Sacar un chip (`removeOption`) NO la cierra — el asesor
    // sigue tipeando en el mismo campo multi-select.
    return event.reason === 'removeOption' || !state.editing
      ? state
      : restingWhileFocused(state);
  }
}

/**
 * Qué tecleó el asesor cuando la sesión de tipeo arranca sobre un campo en
 * reposo. El input mostraba la etiqueta de lo elegido y MUI reporta el
 * CONTENIDO ENTERO del campo, así que lo tecleado viene pegado a esa
 * etiqueta: al final si el cursor quedó donde MUI lo dejó tras elegir
 * ("Madrid" + "bar"), al principio si el asesor lo movió ("bar" + "Madrid").
 * Reemplazar la selección entera (seleccionar todo y tipear encima) llega ya
 * limpio y se devuelve tal cual, igual que una edición en el medio: lo que
 * el campo muestra es lo que se busca, nunca algo distinto.
 */
export function textTypedOverSelection(text: string, selectedLabel: string): string {
  if (!selectedLabel) return text;
  if (text.startsWith(selectedLabel)) return text.slice(selectedLabel.length);
  if (text.endsWith(selectedLabel)) return text.slice(0, text.length - selectedLabel.length);
  return text;
}

/**
 * ¿El foco entró o salió DEL CONTROL, o solo se movió adentro? La X de
 * limpiar y la flecha del desplegable son botones dentro del mismo div raíz:
 * al clickearlos el input pierde el foco y lo recupera enseguida, y tratar
 * eso como "salió del campo" borraría lo que el asesor venía tipeando justo
 * cuando abre la lista para elegir.
 */
export function focusCrossesTheControl(
  control: { contains(node: Node | null): boolean },
  other: Node | null,
): boolean {
  return !other || !control.contains(other);
}

/**
 * El `inputValue` controlado del Autocomplete. `selectedLabel` es lo que hay
 * elegido (`''` en los multi-select: sus selecciones se dibujan como chips,
 * el input queda libre para la próxima).
 */
export function autocompleteInputValue(
  state: AutocompleteFocusState,
  selectedLabel: string,
): string {
  return state.editing ? state.text : selectedLabel;
}

/**
 * Qué texto hay que buscar. Fuera de una sesión de tipeo no hay búsqueda:
 * ni al restaurar, ni al elegir, ni después de salir del campo (invariante 3).
 * Tener el foco tampoco alcanza: quedarse parado en el campo después de
 * elegir no vuelve a consultar el catálogo (invariante 4).
 */
export function autocompleteQuery(state: AutocompleteFocusState): string {
  return state.editing ? state.text : '';
}

/**
 * ¿Este cambio de valor lo pidió el asesor? Todo sí, menos el borrado que
 * MUI dispara solo porque el input quedó vacío mientras se tipeaba otra
 * búsqueda (invariante 2): ese se descarta y el valor anterior sigue en pie
 * hasta que se elija otro o se apriete la X.
 */
export function autocompleteAcceptsValueChange(
  state: AutocompleteFocusState,
  reason: AutocompleteChangeReason,
): boolean {
  return reason !== 'clear' || state.clearRequested;
}
